import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useSearchParams, useNavigate } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { ClickstreamScene } from '@zenbusiness/application-commons-clickstream';
import { useSlideCheckout } from '@zenbusiness/application-commons-ecommerce';
import { FederatedModule } from '@zenbusiness/application-commons-federation';
import { usePriceList } from '@zenbusiness/application-commons-pricelist';

import { FEDERATED } from '#client/build-env';
import { useDashboardPath } from '#client/hooks/useDashboardPath';
import { useUrlResolver } from '#client/hooks/useUrlResolver';
import { useProductCoupon } from '#client/pages/ProductLanding/hooks/useProductCoupon';

const FLOW_ID = 'dashboard-checkout';
const FLOW_VERSION = 'v1';

// Map of services to their custom slot components
const SERVICE_SLOTS = {
  business_license_filing: {
    subscription: <FederatedModule {...FEDERATED.ComplianceUi} exportName="BusinessLicenseFilingCheckoutCallout" />,
  },
  // SQSP domain reseller — Terms & Privacy disclosure at point of purchase
  domain_name_reg_sqsp: {
    subscription: <FederatedModule {...FEDERATED.WebsitesUi} exportName="DomainCheckoutTOS" />,
  },
};

const SlideCheckout = () => {
  const navigate = useNavigate();
  const { open, openCheckout, closeCheckout, onClose, servicePricingUuids, serviceNames, servicesToAddToCart } =
    useSlideCheckout();
  const { get, priceListUuid, priceListName } = usePriceList();
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectPathRef = useRef(null);
  const dashboardPath = useDashboardPath();
  const resolve = useUrlResolver();
  const { entitlements } = useBusinessEntity();

  // Parse URL parameters for checkout
  const checkoutName = searchParams.get('checkout');
  const filter = searchParams.get('filter');
  const parsedFilter = useMemo(() => (filter ? JSON.parse(filter) : { interval: 'annual' }), [filter]);

  // Check for applicable coupon for URL parameter checkout
  const { serviceToAddToCart: urlParamServiceToAdd } = useProductCoupon({
    entitlements,
    serviceCode: checkoutName || '',
    interval: parsedFilter.interval || 'annual',
  });

  const onCloseCheckout = useCallback(
    ({ isPurchaseComplete }) => {
      closeCheckout();
      if (isPurchaseComplete && redirectPathRef.current) {
        // Hard-navigate (full reload) when the resolved entry sets forceReload; otherwise SPA-navigate.
        if (redirectPathRef.current.forceReload) {
          window.location.href = redirectPathRef.current.path;
        } else {
          navigate(redirectPathRef.current.path);
        }
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [navigate],
  );

  // Listen for the parameter "checkout" in the URL to open the dashboard checkout modal
  // filter must be in the following format: &filter={"interval":"annual","price":200}
  // no filter will default to annual pricing if there is more than one service pricing option
  useEffect(() => {
    if (priceListUuid && checkoutName) {
      const requestedServicePricing = get([checkoutName], parsedFilter);

      const requestedServicePricingUuids = requestedServicePricing
        ?.map(({ pricing }) => pricing?.servicePricingUuid)
        .flat();

      if (requestedServicePricingUuids?.length) {
        // Set the redirect path in the ref before opening checkout. The canonical
        // resolver owns the service→destination mapping (checkout_slide context);
        // an unmapped service falls back to the dashboard root.
        const { url, forceReload, found } = resolve(checkoutName, { context: 'checkout_slide' });
        redirectPathRef.current = found ? { path: url, forceReload } : { path: dashboardPath, forceReload: false };

        // Use servicesToAddToCart format when coupon is available (includes coupon UUID)
        if (urlParamServiceToAdd) {
          openCheckout({
            servicesToAddToCart: [urlParamServiceToAdd],
            onClose: onCloseCheckout,
          });
        } else {
          // Fall back to legacy format when no coupon is applicable
          openCheckout({
            serviceNames: [checkoutName],
            servicePricingUuids: requestedServicePricingUuids,
            interval: parsedFilter.interval || 'annual',
            onClose: onCloseCheckout,
          });
        }

        // Remove the checkout parameter from the URL
        setSearchParams((params) => {
          params.delete('checkout');
          params.delete('filter');
          return params;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, priceListUuid, urlParamServiceToAdd]);

  const slots = useMemo(
    () => (SERVICE_SLOTS[serviceNames[0]] || SERVICE_SLOTS[servicesToAddToCart[0]?.serviceName]) ?? null,
    [servicesToAddToCart, serviceNames],
  );

  return priceListUuid ? (
    <ClickstreamScene
      flowId={FLOW_ID}
      flowVersion={FLOW_VERSION}
      flowPriceListUuid={priceListUuid}
      flowPriceListName={priceListName}
    >
      <FederatedModule
        {...FEDERATED.EcommerceUi}
        exportName="SlideCheckout"
        isOpen={open}
        onClose={onClose}
        priceListUuid={priceListUuid}
        servicePricingUuids={servicePricingUuids}
        slots={slots}
      />
    </ClickstreamScene>
  ) : null;
};

export default SlideCheckout;
